import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SocketStatusChangeEvent } from '@m43/sockets-mark43';
import { StreamMap } from '../../socket/stream-types';
import { createStreamListener, useStream, useSocketStatus } from '../../socket/use-stream';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { EsriMapStore } from '../stores/esri-map-store';
import { useEsriMapResource } from './use-esri-map-resource';
import { PromiseBuffer } from '../../common/promises/promise-buffer';
import { useWorkspaceWindowType } from '../../common/hooks/use-workspace-window-type';
import { useEsriMapBootstrap } from './use-esri-map-bootstrap';
import { merge } from 'lodash';
import { MapViewRequest, NudgeObject, NudgeObjectDto, UnitStateGroupEnum } from '@mark43/cad-api';
import {
    MAP_VIEW_REQUEST_PROMISE_BUFFER_DELAY,
    UNIT_GPS_BULK_UPDATE_DELAY,
} from '../configuration';
import { useBulkGpsUpdate } from './use-bulk-gps-update';
import { useEsriMapLoadTestSimulator } from './use-esri-map-load-test-simulator';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { globalAttributes } from '../../common/global-attributes';
import { useIntegratedStreams } from '../../common/hooks/use-integrated-streams';
import { runInAction } from 'mobx';
import { useThemeStore } from '~/theme/use-theme-store';

export function useIntegratedEsriMapStore() {
    const { isCadElectron, isCad } = useApplicationType();
    const {
        disableLegacyMapAndSearches,
        mapWindowEnabled,
        showUnitLocationWhenGpsIsNotAvailable,
        showMobileDevicesOnMap,
        showMobileDevicesPolylinesOnMap,
        amlUser,
        mapViewStreamEnabled,
        rollOutPersistWorkspaceMapFiltersEnabled,
    } = useEnabledFeatures();
    const windowType = useWorkspaceWindowType();
    const { cadDepartmentAgencyView } = useBootstrapState();

    const { agencyTypeGlobalAttrId } = cadDepartmentAgencyView;

    const isPoliceAgency = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;

    const mapStoreRef = useRef<EsriMapStore>();

    const {theme} = useThemeStore();

    if (mapStoreRef.current === undefined) {
        mapStoreRef.current = new EsriMapStore({
            getEnabledFeaturesConfig: () => ({
                showUnitLocationWhenGpsIsNotAvailable,
                showMobileDevicesOnMap,
                showMobileDevicesPolylinesOnMap,
                amlUser,
                rollOutPersistWorkspaceMapFiltersEnabled,
            }),
            mapMode: theme === 'dark' ? 'DARK_MODE' : 'LIGHT_MODE',
        });
    }

    const mapStore = mapStoreRef.current;

    const { getMapViewsById } = useEsriMapResource();
    const esriMapStoreBootstrapDataQuery = useEsriMapBootstrap(mapStore);

    const [promiseBuffer] = useState(
        () =>
            new PromiseBuffer<MapViewRequest>({
                delay: MAP_VIEW_REQUEST_PROMISE_BUFFER_DELAY,
                callback: (inputs) => {
                    // Dedupes and merges IDs
                    return updateMapViews(
                        inputs.reduce((acc, curr) => merge({ ...acc }, { ...curr }))
                    );
                },
            })
    );

    const { addToBulkGpsUpdate } = useBulkGpsUpdate(
        UNIT_GPS_BULK_UPDATE_DELAY,
        (pendingGpsUpdateMap) =>
            mapStore.updateMapViewportUnitGpsLocations(
                Array.from(pendingGpsUpdateMap.values()),
                promiseBuffer
            )
    );

    useEffect(() => {
        promiseBuffer.start();
    }, [promiseBuffer]);

    async function updateMapViews(data: MapViewRequest): Promise<void> {
        getMapViewsById(data).then((resp) => {
            if (resp?.data?.mapViewportCompositeView) {
                const {
                    units: mapViewportUnits,
                    events: mapViewportEvents,
                    hospitals: mapViewportHospitals,
                } = resp.data.mapViewportCompositeView;
                const {
                    units: mapLedgerUnits,
                    events: mapLedgerEvents,
                    hospitals: mapLedgerHospitals,
                } = resp.data.mapLedgerCompositeView;

                mapStore.upsertMapViewportEvents(mapViewportEvents);
                mapStore.upsertMapViewportUnits(mapViewportUnits);
                mapStore.upsertMapViewportHospitals(mapViewportHospitals);

                mapStore.upsertMapLedgerEvents(mapLedgerEvents);
                mapStore.upsertMapLedgerUnits(mapLedgerUnits);
                mapStore.upsertMapLedgerHospitals(mapLedgerHospitals);
            }
        });
    }

    // we need to prevent nudge followup when we get these updates for a class, that we are not allowed to see (for EMS or Fire users in FR)
    const filterOutPoliceEntitiesForEmsOrFireUsers = useCallback(
        (nudge: NudgeObject) => {
            if (isCad || isPoliceAgency) {
                return true;
            }

            // nudge.g is the agency type global number
            if (nudge.g) {
                return nudge.g !== globalAttributes.agencyTypeGlobal.POLICE;
            }

            // nudge.e is the eventId
            if (nudge.e) {
                const emsOrFireEvent = mapStore.getMapViewportEvent(nudge.e);
                return emsOrFireEvent ? true : false;
            }

            // nudge.u is the unitId
            if (nudge.u) {
                const emsOrFireUnit = mapStore.getMapViewportUnit(nudge.u);
                return emsOrFireUnit ? true : false;
            }
        },
        [isCad, isPoliceAgency, mapStore]
    );

    const getEventIdsToEvict = useCallback(
        (nudges: NudgeObject[]): number[] => {
            // nudge.e is the eventId
            return nudges
                .filter(
                    (eventNudge): eventNudge is NudgeObject & { e: number } =>
                        eventNudge.e !== undefined
                )
                .filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                .map((nudge) => nudge.e);
        },
        [filterOutPoliceEntitiesForEmsOrFireUsers]
    );

    function isOffDuty(unitStateGroup?: number) {
        return unitStateGroup === UnitStateGroupEnum.OFF_DUTY.value;
    }

    const getUnitIdsToEvict = useCallback(
        (nudges: NudgeObject[]): number[] => {
            // nudge.u is the unitId
            return nudges
                .filter(
                    (unitStatusNudge): unitStatusNudge is NudgeObject & { u: number } =>
                        unitStatusNudge.u !== undefined && isOffDuty(unitStatusNudge.usg)
                )
                .filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                .map((nudge) => nudge.u);
        },
        [filterOutPoliceEntitiesForEmsOrFireUsers]
    );

    const getUnitIdsToUpdate = useCallback(
        (payload: NudgeObjectDto): number[] => {
            return [
                ...(payload.map['U']
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .map((nudge) => nudge.u) ?? []), // UNIT_KEY
                ...(payload.map['E']
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .flatMap((nudge) => nudge.assignedUnitIds) ?? []), // EVENT_KEY
                ...(payload.map['ULI']
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .map((nudge) => nudge.u) ?? []), // UNIT_LOCATION_INFO
                ...(payload.map['UUL']
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .map((nudge) => nudge.u) ?? []), // USER_UNIT_LINK_KEY
                ...(payload.map['USHIFT']
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .map((nudge) => nudge.u) ?? []), // UNIT_SHIFT
                ...(payload.map['USL']
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .map((nudge) => nudge.u) ?? []), // UNIT_STATUS_LINK_KEY
                ...(payload.map['UST']
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .map((nudge) => nudge.u) ?? []), // UNIT_STATE_KEY
                ...(payload.map['T']
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .map((nudge) => nudge.u) ?? []), // TRANSPORT_KEY
                ...(payload.map['USS'] // UNIT_STATUS_KEY
                    ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                    .filter((nudge) => !isOffDuty(nudge.usg))
                    .map((nudge) => nudge.u) ?? []),
            ].filter((id) => id !== undefined) as number[];
        },
        [filterOutPoliceEntitiesForEmsOrFireUsers]
    );

    const listener = useMemo(() => {
        if (!disableLegacyMapAndSearches && !mapWindowEnabled) {
            return;
        }
        if (isCadElectron && windowType !== 'MAP_V2') {
            return;
        }

        // Nudge websocket for MapViewportUnit, MapViewportEvent, MapLedgerUnit and MapLedgerEvent.
        return createStreamListener({
            streamNames: ['nudge', 'gps'],
            onMessage: (message) => {
                if (message.streamName === 'nudge') {
                    if (mapViewStreamEnabled) return;
                    // events updated
                    const eventIdsToUpdate: number[] =
                        // EVENT_KEY
                        message.payload.map['E']
                            ?.filter(filterOutPoliceEntitiesForEmsOrFireUsers)
                            .map((nudge) => nudge.id) ?? [];

                    // units updated
                    const unitIdsToUpdate: number[] = getUnitIdsToUpdate(message.payload);

                    if (eventIdsToUpdate.length > 0 || unitIdsToUpdate.length > 0) {
                        promiseBuffer.enqueue({
                            eventIds: eventIdsToUpdate,
                            unitIds: unitIdsToUpdate,
                            hospitalIds: [],
                            dispatchAreaIds: [],
                        });
                    }

                    // events removed
                    const eventIdsToEvict: number[] = getEventIdsToEvict(
                        // EVENT_CLEARING
                        message.payload.map['EC'] ?? []
                    );

                    // units removed
                    const unitIdsToEvict: number[] = getUnitIdsToEvict(
                        // UNIT_STATUS_KEY
                        message.payload.map['USS'] ?? []
                    );

                    if (eventIdsToEvict.length > 0 || unitIdsToEvict.length > 0) {
                        mapStore.evictUnitEntities(unitIdsToEvict);
                        mapStore.evictEventEntities(eventIdsToEvict);
                    }

                    // hospitals updated
                    const hospitalIdsToUpdate: number[] =
                        // HOSPITAL
                        message.payload.map['H']?.map((nudge) => nudge.id) ?? [];

                    // hospitals can only be updated, but not removed
                    if (hospitalIdsToUpdate.length > 0) {
                        promiseBuffer.enqueue({
                            eventIds: [],
                            unitIds: [],
                            hospitalIds: hospitalIdsToUpdate,
                            dispatchAreaIds: [],
                        });
                    }
                }

                if (message.streamName === 'gps') {
                    addToBulkGpsUpdate(message.payload);
                }
            },
        });
    }, [
        disableLegacyMapAndSearches,
        mapWindowEnabled,
        isCadElectron,
        windowType,
        mapViewStreamEnabled,
        filterOutPoliceEntitiesForEmsOrFireUsers,
        getUnitIdsToUpdate,
        getEventIdsToEvict,
        getUnitIdsToEvict,
        promiseBuffer,
        mapStore,
        addToBulkGpsUpdate,
    ]);

    useStream(listener);

    const socketStatusListener = useMemo(
        () => ({
            invokeOnInitialize: false,
            onSocketStatusChange(
                socketStatusChangeEvent: SocketStatusChangeEvent<StreamMap, keyof StreamMap>
            ) {
                if (mapViewStreamEnabled) return;
                if (socketStatusChangeEvent.status === 'CONNECTED') {
                    promiseBuffer.stop();
                    promiseBuffer.reset();
                    esriMapStoreBootstrapDataQuery.refetch().then(() => {
                        promiseBuffer.start();
                    });
                }
            },
        }),
        [esriMapStoreBootstrapDataQuery, promiseBuffer, mapViewStreamEnabled]
    );

    useSocketStatus(socketStatusListener);

    useEsriMapLoadTestSimulator(mapStore, addToBulkGpsUpdate);

    useIntegratedStreams({
        enabled: mapViewStreamEnabled,
        streamNames: [isPoliceAgency ? 'mapViews' : 'mapViewsNoCjis'],
        query: esriMapStoreBootstrapDataQuery,
        onMessage(message) {
            const {
                eventIdsToEvict,
                mapLedgerEventViews,
                mapLedgerHospitalViews,
                mapLedgerUnitViews,
                mapViewportEventViews,
                mapViewportHospitalViews,
                mapViewportUnitViews,
                unitIdsToEvict,
            } = message.payload;
            runInAction(() => {
                mapStore.upsertMapViewportEvents(mapViewportEventViews);
                mapStore.upsertMapViewportUnits(mapViewportUnitViews);
                mapStore.upsertMapViewportHospitals(mapViewportHospitalViews);
                mapStore.upsertMapLedgerEvents(mapLedgerEventViews);
                mapStore.upsertMapLedgerUnits(mapLedgerUnitViews);
                mapStore.upsertMapLedgerHospitals(mapLedgerHospitalViews);
                mapStore.evictUnitEntities(unitIdsToEvict);
                mapStore.evictEventEntities(eventIdsToEvict);
            });
        },
    });

    return mapStore;
}
